import { gerarAmigos } from '../mocks/amigosMock';
import api from './api';

// Configuração do modo teste
const MODO_TESTE = process.env.REACT_APP_MOCK_API === 'true';
const QUANTIDADE_AMIGOS_TESTE = 15;

export const amigosService = {
  listarAmigos: async (userId) => {
    try {
      if (!userId) {
        throw new Error('ID do usuário não fornecido');
      }

      if (MODO_TESTE) {
        const amigosMock = gerarAmigos(QUANTIDADE_AMIGOS_TESTE);
        return { amigos: amigosMock };
      }

      const response = await api.get(`/usuarios/${userId}/amigos`);
      
      // Garantir que os dados retornados tenham a estrutura esperada
      const amigos = response.data.amigos?.map(amigo => ({
        id: amigo.id,
        nome: amigo.nome,
        nickname: amigo.nickname,
        email: amigo.email,
        avatar: amigo.avatar, // Remover fallback para ui-avatars
        publico: amigo.publico,
        esportes: amigo.esportes || []
      })) || [];

      return { amigos };
    } catch (error) {
      console.error('Erro ao listar amigos:', error);
      throw new Error(error.response?.data?.mensagem || 'Erro ao carregar lista de amigos');
    }
  },

  buscarAmigo: async (userId, amigoId) => {
    try {
      const response = await api.get(`/usuarios/${userId}/amigos/${amigoId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar amigo:', error);
      throw new Error('Erro ao carregar dados do amigo');
    }
  },

  listarAmigosDoAmigo: async (amigoId) => {
    try {
      const response = await api.get(`/amizades/${amigoId}/amigos/publicos`);
      return response.data.amigos || [];
    } catch (error) {
      console.error('Erro ao listar amigos do amigo:', error);
      throw new Error('Erro ao carregar lista de amigos');
    }
  },

  marcarComoPublico: async (userId, amigoId, isPublico) => {
    try {
      // Alterando de PATCH para PUT
      await api.put(`/amizades/${userId}/amigos/${amigoId}/visibilidade`, {
        publico: isPublico
      });
      return true;
    } catch (error) {
      console.error('Erro ao atualizar visibilidade do amigo:', error);
      throw new Error('Erro ao atualizar visibilidade');
    }
  },

  enviarSolicitacaoAmizade: async (userId, amigoId) => {
    try {
      await api.post(`/amizades/${userId}/solicitacoes-amizade`, {
        destinatarioId: amigoId
      });
      return true;
    } catch (error) {
      console.error('Erro ao enviar solicitação de amizade:', error);
      throw new Error('Erro ao enviar solicitação de amizade');
    }
  },

  listarSolicitacoesAmizade: async (userId) => {
    try {
      const response = await api.get(`/amizades/${userId}/solicitacoes-amizade`);
      const solicitacoes = response.data.solicitacoes?.map(solicitacao => {
        // Converter o formato especial de timestamp para Date
        let dataEnvio = new Date();
        
        if (solicitacao.dataEnvio) {
          // Caso seja o formato {_seconds, _nanoseconds}
          if (solicitacao.dataEnvio._seconds) {
            dataEnvio = new Date(solicitacao.dataEnvio._seconds * 1000); // Converter segundos para milissegundos
          } 
          // Caso seja um timestamp numérico direto
          else if (typeof solicitacao.dataEnvio === 'number') {
            dataEnvio = new Date(solicitacao.dataEnvio);
          }
          // Caso seja uma string ISO
          else if (typeof solicitacao.dataEnvio === 'string') {
            dataEnvio = new Date(solicitacao.dataEnvio);
          }
        }
        
        return {
          id: solicitacao.id,
          remetente: {
            id: solicitacao.remetente.id,
            nome: solicitacao.remetente.nome,
            email: solicitacao.remetente.email,
            avatar: solicitacao.remetente.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(solicitacao.remetente.nome)}&background=random`
          },
          dataEnvio: dataEnvio // Agora é um objeto Date válido
        };
      }) || [];
      
      return { solicitacoes };
    } catch (error) {
      console.error('Erro ao listar solicitações de amizade:', error);
      throw new Error('Erro ao carregar solicitações de amizade');
    }
  },

  aceitarSolicitacaoAmizade: async (userId, solicitacaoId) => {
    try {
      await api.post(`/amizades/${userId}/solicitacoes-amizade/${solicitacaoId}/aceitar`);
      return true;
    } catch (error) {
      console.error('Erro ao aceitar solicitação de amizade:', error);
      throw new Error('Erro ao aceitar solicitação de amizade');
    }
  },

  rejeitarSolicitacaoAmizade: async (userId, solicitacaoId) => {
    try {
      await api.post(`/amizades/${userId}/solicitacoes-amizade/${solicitacaoId}/rejeitar`);
      return true;
    } catch (error) {
      console.error('Erro ao rejeitar solicitação de amizade:', error);
      throw new Error('Erro ao rejeitar solicitação de amizade');
    }
  },

  async verificarSolicitacoesPendentes(usuarioId, destinatariosIds) {
    try {
      const response = await api.post(`/amizades/${usuarioId}/solicitacoes/pendentes`, {
        destinatariosIds
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar solicitações pendentes:', error);
      throw error;
    }
  },

  // Buscar pessoas sugeridas (amigos dos amigos)
  buscarPessoasSugeridas: async (userId) => {
    try {
      const response = await api.get(`/amizades/${userId}/sugestoes`);
      
      // Processar e ordenar os dados retornados
      const pessoasSugeridas = response.data.sugestoes?.map(pessoa => ({
        id: pessoa.id,
        nome: pessoa.nome,
        apelido: pessoa.apelido || pessoa.nickname,
        email: pessoa.email,
        avatar: pessoa.avatar,
        publico: pessoa.publico,
        esportes: pessoa.esportes || [],
        amigosEmComum: pessoa.amigosEmComum || [],
        quantidadeAmigosEmComum: pessoa.amigosEmComum?.length || 0
      })) || [];

      // Ordenar por quantidade de amigos em comum (decrescente)
      pessoasSugeridas.sort((a, b) => b.quantidadeAmigosEmComum - a.quantidadeAmigosEmComum);

      return { sugestoes: pessoasSugeridas };
    } catch (error) {
      console.error('Erro ao buscar pessoas sugeridas:', error);
      throw new Error(error.response?.data?.mensagem || 'Erro ao carregar sugestões de pessoas');
    }
  }
};
